import {jsx} from 'panel';

import {ContextPanelEl} from 'common/widgets/context-panel';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {EntityTypeNameSingular, SharableEntityType} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {promptSharing} from 'common/widgets/sharing';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {promptCustomEvents} from 'common/widgets/custom-event-modal/utils';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {ProjectFeatureFlag} from 'common/report/constants';

import {updateEntityDescription} from '../utils/util';
import {TRACKING_EVENTS} from '../constants';
import type {ContextPanelRendererParams} from '../child-menu';
import {getCustomEventMenuItem} from '../contexts/events-controller';

export const createContextPanelEventHandlers = ({
  track,
  handleSelect,
  handleDelete,
  additionalArgs = {},
  handleHoverIn = null,
  handleHoverOut = null,
}: Partial<ContextPanelRendererParams>) => {
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    if (ev.detail.name === `editCustomEvent`) {
      const {appliedValue: customEvent, deletedId} = await promptCustomEvents({
        open: true,
        'all-data-groups': additionalArgs[`all-data-groups`],
        'project-has-people': additionalArgs[`project-has-people`],
        'project-id': getProjectId(),
        'workspace-id': getWorkspaceId(),
        'track-name': additionalArgs[`track-name`],
        'selected-custom-event-id': ev.detail.item.id,
      });

      if (customEvent) {
        handleSelect({item: getCustomEventMenuItem(customEvent).value});
      } else if (deletedId) {
        handleDelete({id: deletedId});
      }
    } else if (ev.detail.name === `shareCustomEvent`) {
      const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
      track(eventName, props, false, shareEventPrefix);

      promptSharing({
        type: SharableEntityType.CustomEvent,
        id: ev.detail.item.id,
        'project-id': parseInt(getProjectId(), 10),
        'is-superadmin': ev.detail.item.superadminOverrideShare,
      });
    } else if (ev.detail.name === `viewSampleEvents` && ev.detail.item.url) {
      track(TRACKING_EVENTS.SAMPLE_EVENT, {}, false);
      if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar)) {
        openEventsSidebar({url: ev.detail.item.url});
      } else {
        window.open(ev.detail.item.url, `_blank`);
      }
    } else if (ev.detail.name === `viewEntity` && ev.detail.item.url) {
      window.open(ev.detail.item.url, `_blank`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: CustomEvent) => {
    const eventStoreKey = detail.item.name;
    const IdName = detail.item.type === EntityTypeNameSingular.Event ? `id` : `customEventId`;

    const definition = eventDefinitionsStore.value[eventStoreKey];
    const descriptionBeforeUpdate = definition?.description ?? null;
    const count = definition?.count || 0;

    let eventDefinition = {[IdName]: detail.item.id, description: detail.description};
    if (!detail.item.id) {
      eventDefinition = {name: detail.item.name, description: detail.description};
    }
    const updateRequestObj = {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      eventDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      eventDefinition: {...updateRequestObj.eventDefinition, description: descriptionBeforeUpdate},
    };

    await updateEntityDescription(
      eventDefinitionsStore,
      detail.description,
      eventStoreKey,
      updateRequestObj,
      undoRequestObj,
      detail.item.type,
      track,
    );
    eventDefinitionsStore.update({
      [eventStoreKey]: {
        ...eventDefinitionsStore.value[eventStoreKey],
        count,
      },
    });
  };

  return {
    hoverIn: handleHoverIn,
    hoverOut: handleHoverOut,
    tooltipContextAction: handleContextPanelAction,
    tooltipContextEditDescription: handleContextPanelEditDescription,
  };
};

/**
 * Renders and handles context panel actions for an Event resource
 */
export const renderEventItemContextPanel = ({
  tooltipData,
  contextPanelAttrs,
  handleSelect,
  handleDelete,
  handleHoverIn,
  handleHoverOut,
  track,
  additionalArgs = {},
  contextPanelProps = {},
}: ContextPanelRendererParams) => {
  if (!tooltipData) {
    return null;
  }

  return (
    <ContextPanelEl
      attrs={{
        ...contextPanelAttrs,
        'tooltip-data': tooltipData,
        elRef: `contextPanel`,
      }}
      props={contextPanelProps}
      on={createContextPanelEventHandlers({
        track,
        handleHoverIn,
        handleHoverOut,
        handleSelect,
        handleDelete,
        additionalArgs,
      })}
    />
  );
};
